import { errorWords } from "@alm/domain";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { optionalOneOf, optionalString } from "@/lib/search";
import { Search } from "@/lib/icons";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/app/lists/empty-state";
import { Input } from "@/components/ui/input";
import { PageHeader } from "@/components/app/shell/page-header";
import { Skeleton } from "@/components/ui/skeleton";
import { useFacility } from "@/lib/facility/facility";
import { orpc } from "@/lib/orpc";
import { guardRoute } from "@/lib/policy/route-guard";
import { DriveRail } from "./-components/drive-rail";
import { FolderBrowser } from "./-components/folder-browser";
import type { LibraryView } from "./-components/library";
import { MoveDialog, type MoveTarget } from "./-components/move-dialog";
import { Recent } from "./-components/recent";
import { RenameDialog } from "./-components/rename-dialog";
import type { RenameTarget } from "@/routes/_authenticated/_knowledge/knowledgebase/-components/rename-dialog-types";
import { SearchResults } from "./-components/search-results";
import { ShareDialog, type ShareTargetRef } from "./-components/share-dialog";
import { SharedWithMe } from "./-components/shared-with-me";
import { Starred } from "./-components/starred";
import { TrashPanel } from "./-components/trash-panel";
import { VersionsDialog, type VersionsTarget } from "./-components/versions-dialog";

const LIBRARY_VIEWS: readonly LibraryView[] = ["browse", "shared", "starred", "recent", "trash"];

/** The drive and folder live in the URL so a link to the current folder can be shared. */
export const Route = createFileRoute("/_authenticated/_knowledge/knowledgebase/")({
  validateSearch: (
    search: Record<string, unknown>,
  ): { drive?: string; folder?: string; view?: LibraryView; q?: string } => ({
    drive: optionalString(search.drive),
    folder: optionalString(search.folder),
    view: optionalOneOf(LIBRARY_VIEWS, search.view),
    q: optionalString(search.q),
  }),
  beforeLoad: ({ context }) => guardRoute(context.me, "knowledgebase"),
  component: Knowledgebase,
});

const TITLES: Record<LibraryView, string> = {
  browse: "Knowledgebase",
  shared: "Shared with me",
  starred: "Starred",
  recent: "Recent",
  trash: "Recycle bin",
};

function Knowledgebase() {
  const { drive, folder, view, q } = Route.useSearch();
  const navigate = Route.useNavigate();
  const { facilityId } = useFacility();

  const [renaming, setRenaming] = useState<RenameTarget | null>(null);
  const [moving, setMoving] = useState<MoveTarget | null>(null);
  const [sharing, setSharing] = useState<ShareTargetRef | null>(null);
  const [versioning, setVersioning] = useState<VersionsTarget | null>(null);
  const [term, setTerm] = useState(q ?? "");

  const drives = useQuery(orpc.knowledge.files.drives.queryOptions({ input: { facilityId } }));
  const shared = useQuery(orpc.knowledge.files.sharedWithMe.queryOptions({ input: {} }));

  const rows = drives.data ?? [];
  const myDrive = rows.find((row) => row.scope === "user");
  const activeView: LibraryView = view ?? "browse";
  const activeDriveId = drive ?? myDrive?.id ?? null;

  const go = (next: {
    view?: LibraryView;
    driveId?: string | null;
    folderId?: string | null;
    query?: string | null;
  }) =>
    navigate({
      search: {
        view: next.view ?? activeView,
        drive: next.driveId === undefined ? (drive ?? undefined) : (next.driveId ?? undefined),
        folder: next.folderId === undefined ? (folder ?? undefined) : (next.folderId ?? undefined),
        q: next.query === undefined ? (q ?? undefined) : (next.query ?? undefined),
      },
    });

  /* Opening something from "Shared with me", Starred or a search lands in the
     owner's drive at that folder — the same browser, a different starting point. */
  const openFolder = (driveId: string, folderId: string) =>
    go({ view: "browse", driveId, folderId, query: null });

  const sharedCount = (shared.data?.folders.length ?? 0) + (shared.data?.files.length ?? 0);
  const searching = Boolean(q?.trim());

  return (
    <>
      <PageHeader
        title={TITLES[activeView]}
        meta="Your own folders, the things people shared with you, and the libraries your building and organization keep."
        actions={
          <form
            className="flex items-center gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              go({ query: term.trim() || null });
            }}
          >
            <Input
              value={term}
              placeholder="Search the library"
              aria-label="Search the library"
              className="w-44 sm:w-56"
              onChange={(event) => setTerm(event.target.value)}
            />
            <Button type="submit" variant="outline" size="icon" aria-label="Search">
              <Search className="size-4" />
            </Button>
          </form>
        }
      />

      {/* A failed drive list is its own error screen, not empty: `listDrives` always returns at
          least the caller's own and the organization's drive, so "no drives" only means the call
          failed. */}
      {drives.isError ? (
        <div className="mt-6">
          <EmptyState
            title="The library could not be opened"
            description={errorWords(drives.error)}
            action={
              <Button variant="outline" onClick={() => void drives.refetch()}>
                Try again
              </Button>
            }
          />
        </div>
      ) : (
        <div className="mt-6 flex flex-col gap-6 sm:flex-row">
          {drives.isLoading ? (
            <Skeleton className="h-64 w-full sm:w-56" />
          ) : (
            <DriveRail
              drives={rows}
              activeDriveId={activeDriveId}
              view={activeView}
              sharedCount={sharedCount}
              onPick={(next) =>
                go({ view: next.view, driveId: next.driveId ?? null, folderId: null, query: null })
              }
            />
          )}

          <div className="min-w-0 flex-1">
            {searching && q ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-muted-foreground text-sm">
                    Results for <span className="font-medium">“{q}”</span>
                  </p>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setTerm("");
                      go({ query: null });
                    }}
                  >
                    Clear
                  </Button>
                </div>
                <SearchResults query={q} facilityId={facilityId} onOpenFolder={openFolder} />
              </div>
            ) : activeView === "shared" ? (
              <SharedWithMe onOpenFolder={openFolder} />
            ) : activeView === "starred" ? (
              <Starred onOpenFolder={openFolder} />
            ) : activeView === "recent" ? (
              <Recent facilityId={facilityId} />
            ) : activeView === "trash" ? (
              activeDriveId ? (
                <TrashPanel driveId={activeDriveId} />
              ) : (
                <EmptyState
                  title="Choose a library"
                  description="Pick one on the left to see its recycle bin."
                />
              )
            ) : activeDriveId ? (
              <FolderBrowser
                driveId={activeDriveId}
                folderId={folder ?? null}
                onNavigate={(next) => go({ view: "browse", folderId: next })}
                onRename={setRenaming}
                onMove={setMoving}
                onShare={setSharing}
                onVersions={setVersioning}
              />
            ) : (
              <EmptyState title="Opening your library…" description="One moment." />
            )}
          </div>
        </div>
      )}

      <RenameDialog target={renaming} onOpenChange={(open) => !open && setRenaming(null)} />
      <MoveDialog target={moving} onOpenChange={(open) => !open && setMoving(null)} />
      <ShareDialog target={sharing} onOpenChange={(open) => !open && setSharing(null)} />
      <VersionsDialog target={versioning} onOpenChange={(open) => !open && setVersioning(null)} />
    </>
  );
}
